// Motion planner (presentation-2 contract sections 4 and 8.2). Pure: turns motion events into a
// timed plan from the theme's motion tokens. The layer executes plans; nothing here touches the DOM.

import type { MotionEvent } from './diff.ts';
import type { Observation } from '../protocol/ui';
import { ownsDecision } from '../model/table';

export type Speed = 'calm' | 'brisk' | 'snappy';

export interface MotionTokens { move: number; flip: number; pulse: number; stagger: number; easing: string }

const TOKENS: Record<Speed, Omit<MotionTokens, 'easing'>> = {
  calm: { move: 520, flip: 420, pulse: 600, stagger: 110 },
  brisk: { move: 380, flip: 300, pulse: 600, stagger: 80 },
  snappy: { move: 280, flip: 220, pulse: 600, stagger: 60 },
};

export const EASING = 'cubic-bezier(.2,.8,.2,1)';
export const FADE_MS = 120;
/** Card backs drawn for one count change. */
export const MAX_COUNT_GHOSTS = 5;

export function motionTokens(speed: Speed = 'brisk'): MotionTokens {
  return { ...TOKENS[speed], easing: EASING };
}

export interface PlannedStep { event: MotionEvent; delay: number; duration: number; units?: number }

export interface Plan {
  mode: 'animate' | 'fade' | 'none';
  steps: PlannedStep[];
  /** Milliseconds until the last step ends. */
  total: number;
}
/** A per-surface budget; omitted leaves existing presentations unchanged. */
export function boundMotion(p:Plan, maximum:number|undefined):Plan{
  if(maximum===undefined||p.total<=maximum)return p;
  if(maximum<=0)return {mode:'none',steps:[],total:0};
  const ratio=maximum/p.total;
  return {...p,steps:p.steps.map(s=>({...s,delay:Math.floor(s.delay*ratio),duration:Math.floor(s.duration*ratio)})),total:maximum};
}

export interface PlanOptions {
  speed?: Speed;
  /** Opponent replay pace, 0.5..2; ignored for own actions. `0` means instant. */
  pace?: number;
  own: boolean;
  reducedMotion: boolean;
  /** Segments per claimed edge, for owner fills. */
  segments?: (id: string) => number;
}

/**
 * Sequencing: departures and moves start together; arrivals follow, staggered; count ghosts and
 * owner fills run alongside; counters pulse at the end of the movement; the turn change comes last.
 */
export function plan(events: MotionEvent[], options: PlanOptions): Plan {
  if (events.length === 0) return { mode: 'none', steps: [], total: 0 };
  const pace = options.own ? 1 : options.pace ?? 1;
  if (pace === 0) return { mode: 'none', steps: [], total: 0 };
  if (options.reducedMotion) return { mode: 'fade', steps: events.map(event => ({ event, delay: 0, duration: FADE_MS })), total: FADE_MS };
  const t = motionTokens(options.speed);
  const scale = (ms: number) => Math.round(ms / pace);
  const steps: PlannedStep[] = [];
  let enterIndex = 0, countIndex = 0;
  const movement = events.some(e => e.type === 'move' || e.type === 'exit') ? t.move : 0;
  for (const event of events) {
    switch (event.type) {
      case 'move': case 'exit':
        steps.push({ event, delay: 0, duration: scale(t.move) });
        break;
      case 'enter':
        steps.push({ event, delay: scale(movement * 0.5 + enterIndex++ * t.stagger), duration: scale(t.move) });
        break;
      case 'count': {
        const units = Math.min(MAX_COUNT_GHOSTS, event.amount);
        steps.push({ event, delay: scale(countIndex++ * t.stagger), duration: scale(t.move + (units - 1) * t.stagger), units });
        break;
      }
      case 'appearance':
        steps.push({ event, delay: 0, duration: scale(t.flip) });
        break;
      case 'owner': {
        const n = Math.max(1, options.segments?.(event.id) ?? 1);
        steps.push({ event, delay: 0, duration: scale(t.flip + (n - 1) * t.stagger), units: n });
        break;
      }
      case 'counter':
        steps.push({ event, delay: scale(movement * 0.6), duration: scale(t.pulse) });
        break;
      case 'turn':
        break;
    }
  }
  const before = steps.reduce((m, s) => Math.max(m, s.delay + s.duration), 0);
  for (const event of events) if (event.type === 'turn') steps.push({ event, delay: Math.max(0, before - scale(t.flip)), duration: scale(t.flip) });
  const total = steps.reduce((m, s) => Math.max(m, s.delay + s.duration), 0);
  return { mode: 'animate', steps, total };
}

/** Pacing queue (section 8.2): at most `cap` revisions wait; beyond that, skip to the latest. */
export function enqueue<T>(queue: T[], item: T, cap = 3): { queue: T[]; skipped: boolean } {
  const next = [...queue, item];
  return next.length > cap ? { queue: [item], skipped: true } : { queue: next, skipped: false };
}

/** Current decision prompts bypass replay pacing; auxiliary availability does not. */
export function enqueueObservation<T>(queue: T[], item: T, observation: Pick<Observation, 'actor' | 'finished'> & { decisionOwners?: unknown }, self: string | null, cap = 3): { queue: T[]; skipped: boolean; prioritized: boolean } {
  const decisionOwners = Array.isArray(observation.decisionOwners)
    ? observation.decisionOwners.filter((id): id is string => typeof id === 'string') : undefined;
  const prioritized = self !== 'spectator' && !observation.finished && ownsDecision({ selfSeat: self, actor: observation.actor, decisionOwners });
  if (prioritized) return { queue: [item], skipped: queue.length > 0, prioritized: true };
  return { ...enqueue(queue, item, cap), prioritized: false };
}
